import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import Modal from '../components/common/Modal';
import { MemberEditForm } from '../components/members/MemberForm';
import AddFamilyMemberForm from '../components/members/AddFamilyMemberForm';
import RecordDeceasedForm from '../components/deceased/DeceasedForm';
import MemberNotes from '../components/members/MemberNotes';
import PaymentHistoryView from '../components/payments/PaymentHistory';
import ReportsDropdown from '../components/members/ReportsDropdown';
import {
  ArrowLeft, Edit, UserX, Users, CreditCard, FileText,
  Phone, Mail, MapPin, Calendar, BadgeCheck, Clock, TrendingUp,
  CheckCircle, XCircle, UserPlus, Shield, Skull, Pencil, Trash2, X,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v ?? 0);
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const RELATION_LABELS = {
  W: 'Wife', H: 'Husband', S: 'Son', D: 'Daughter',
  A: 'Account Holder', B: 'Brother', F: 'Father', M: 'Mother',
};

function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 px-4 sm:px-5 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
        ${active
          ? 'border-blue-600 text-blue-600 bg-blue-50/60'
          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 hover:bg-slate-50'
        }`}>
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden">{label.split(' ')[0]}</span>
    </button>
  );
}

function StatusBadge({ status }) {
  const cls = {
    paid: 'bg-green-100 text-green-700',
    overdue: 'bg-red-100 text-red-700',
    pending: 'bg-amber-100 text-amber-700',
  }[status] || 'bg-slate-100 text-slate-600';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status}</span>;
}

const GRID_YEARS = Array.from({ length: 21 }, (_, i) => 2005 + i); // 2005–2025
const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function LegacyHistoryTab({ memberId, payments, onReload }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [selectedCells, setSelectedCells] = useState([]);
  const [selectionStart, setSelectionStart] = useState(null);
  const [fillValue, setFillValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);

  const getAmount = (year, month) => {
    const p = payments.find(p => {
      const d = new Date(p.payment_month);
      return d.getFullYear() === year && d.getMonth() + 1 === month;
    });
    return p ? parseFloat(p.amount || 0) : 0;
  };

  const handleEdit = () => {
    const data = {};
    GRID_YEARS.forEach(year => {
      for (let m = 1; m <= 12; m++) {
        data[`${year}-${m}`] = getAmount(year, m);
      }
    });
    setEditData(data);
    setSelectedCells([]);
    setSelectionStart(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedCells([]);
    setSelectionStart(null);
  };

  const handleCellChange = (year, month, value) => {
    setEditData(d => ({ ...d, [`${year}-${month}`]: value }));
  };

  const handleCellClick = (year, month, e) => {
    const key = `${year}-${month}`;
    if (e.shiftKey && selectionStart) {
      const y0 = GRID_YEARS.indexOf(selectionStart.year);
      const y1 = GRID_YEARS.indexOf(year);
      const m0 = selectionStart.month - 1;
      const m1 = month - 1;
      const sel = [];
      for (let yi = Math.min(y0, y1); yi <= Math.max(y0, y1); yi++) {
        for (let mi = Math.min(m0, m1); mi <= Math.max(m0, m1); mi++) {
          sel.push(`${GRID_YEARS[yi]}-${mi + 1}`);
        }
      }
      setSelectedCells(sel);
    } else {
      setSelectionStart({ year, month });
      setSelectedCells([key]);
    }
  };

  const handleFillSelected = () => {
    if (!fillValue || selectedCells.length === 0) return;
    const v = parseFloat(fillValue) || 0;
    setEditData(d => {
      const next = { ...d };
      selectedCells.forEach(k => { next[k] = v; });
      return next;
    });
    setFillValue('');
  };

  const handlePaste = (e, year, monthIdx) => {
    e.preventDefault();
    const rows = e.clipboardData.getData('text').split('\n').map(r => r.split('\t'));
    const yIdx = GRID_YEARS.indexOf(year);
    setEditData(d => {
      const next = { ...d };
      rows.forEach((row, ri) => {
        row.forEach((cell, ci) => {
          const ty = GRID_YEARS[yIdx + ri];
          const tm = monthIdx + 1 + ci;
          if (ty && tm >= 1 && tm <= 12) {
            next[`${ty}-${tm}`] = parseFloat(cell.replace(/[^\d.]/g, '')) || 0;
          }
        });
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveProgress(0);
    const tick = setInterval(() => setSaveProgress(p => Math.min(p + 12, 88)), 60);
    try {
      await apiService.saveLegacyPayments(memberId, editData);
      clearInterval(tick);
      setSaveProgress(100);
      setTimeout(() => {
        setSaving(false);
        setSaveProgress(0);
        setIsEditing(false);
        setSelectedCells([]);
        if (onReload) onReload();
      }, 350);
    } catch (err) {
      clearInterval(tick);
      setSaving(false);
      setSaveProgress(0);
      alert(err.message || 'Failed to save legacy payments');
    }
  };

  const totalLegacy = payments
    .filter(p => new Date(p.payment_month).getFullYear() < 2026 && p.status === 'paid')
    .reduce((s, p) => s + parseFloat(p.amount || 0), 0);

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-3.5 flex items-center justify-between shadow-sm">
        <div>
          <p className="font-semibold text-slate-800 text-sm">Legacy Payment History (Pre-2026)</p>
          <p className="text-xs text-slate-500 mt-0.5">Custom amounts from previous system — no overdue calculation</p>
        </div>
        <div className="flex items-center gap-3">
          {totalLegacy > 0 && (
            <span className="text-sm font-semibold text-emerald-600">{fmt(totalLegacy)} total</span>
          )}
          {!isEditing ? (
            <button onClick={handleEdit}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors">
              <Edit className="w-4 h-4" /> Edit History
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={handleCancel} disabled={saving}
                className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
                {saving ? `Saving… ${saveProgress}%` : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bulk-fill bar (edit mode only) */}
      {isEditing && selectedCells.length > 1 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-sm">
          <span className="font-semibold text-blue-800">{selectedCells.length} cells selected</span>
          <input
            type="number" step="0.01" min="0"
            value={fillValue}
            onChange={e => setFillValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleFillSelected()}
            placeholder="Enter amount…"
            className="w-36 px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
          />
          <button onClick={handleFillSelected}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            Fill All
          </button>
          <span className="text-blue-600 text-xs">or press Enter</span>
        </div>
      )}

      {/* Progress bar */}
      {saving && (
        <div className="w-full bg-slate-200 rounded-full h-1.5">
          <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-200" style={{ width: `${saveProgress}%` }} />
        </div>
      )}

      {/* Grid */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 font-semibold text-slate-700 sticky left-0 bg-slate-50 z-10 min-w-[60px]">Year</th>
                {SHORT_MONTHS.map(m => (
                  <th key={m} className="text-center px-1 py-2.5 font-semibold text-slate-600 min-w-[68px]">{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GRID_YEARS.map(year => (
                <tr key={year} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-1 font-semibold text-slate-700 sticky left-0 bg-white hover:bg-slate-50/50 z-10">{year}</td>
                  {SHORT_MONTHS.map((_, mi) => {
                    const month = mi + 1;
                    const key = `${year}-${month}`;
                    const amount = isEditing ? (parseFloat(editData[key]) || 0) : getAmount(year, month);
                    const isSelected = selectedCells.includes(key);
                    return (
                      <td key={month}
                        className={`p-0 text-center ${isSelected ? 'ring-2 ring-inset ring-blue-400 bg-blue-50' : ''}`}>
                        {isEditing ? (
                          <input
                            type="number" step="0.01" min="0"
                            value={editData[key] || ''}
                            onChange={e => handleCellChange(year, month, e.target.value)}
                            onClick={e => handleCellClick(year, month, e)}
                            onPaste={e => handlePaste(e, year, mi)}
                            placeholder="0"
                            className={`w-full h-9 text-center text-sm border-0 focus:ring-1 focus:ring-blue-400 focus:outline-none bg-transparent ${isSelected ? 'bg-blue-50' : ''}`}
                            style={{ appearance: 'textfield', MozAppearance: 'textfield' }}
                          />
                        ) : (
                          <div className={`py-2 px-1 ${amount > 0 ? 'font-semibold text-emerald-600' : 'text-slate-300'}`}>
                            {amount > 0 ? `£${amount.toFixed(0)}` : '–'}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {isEditing && (
          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              Tip: Click a cell then <strong>Shift+click</strong> to select a range. Paste from Excel for bulk entry.
            </p>
          </div>
        )}
      </div>
      <style>{`
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
      `}</style>
    </div>
  );
}

const ROLE_LEVEL = { office_staff: 1, admin: 2, super_user: 3 };
const canAdmin = (role) => (ROLE_LEVEL[role] || 0) >= ROLE_LEVEL.admin;

function ApprovalBadge({ approval_status, void_reason }) {
  if (approval_status === 'voided') {
    return (
      <span title={void_reason ? `Reason: ${void_reason}` : undefined}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 line-through">
        Voided
      </span>
    );
  }
  if (approval_status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Clock className="w-3 h-3" /> Pending
      </span>
    );
  }
  return null;
}

const RELATIONS = [
  { value: 'W', label: 'Wife' }, { value: 'H', label: 'Husband' },
  { value: 'S', label: 'Son'  }, { value: 'D', label: 'Daughter' },
  { value: 'B', label: 'Brother' }, { value: 'F', label: 'Father' }, { value: 'M', label: 'Mother' },
];

function calcAge(dob) {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return age;
}

function FamilyMemberCards({ memberId, family, onReload, onAdd }) {
  const navigate = useNavigate();
  const [editId, setEditId]   = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const startEdit = (fm) => {
    setEditId(fm.id);
    setEditForm({
      name:          fm.name || '',
      relation:      fm.relation || '',
      gender:        fm.gender || '',
      date_of_birth: fm.date_of_birth ? fm.date_of_birth.slice(0, 10) : '',
    });
    setError('');
  };

  const cancelEdit = () => { setEditId(null); setError(''); };

  const saveEdit = async () => {
    if (!editForm.name.trim() || !editForm.relation) { setError('Name and relation are required.'); return; }
    setSaving(true); setError('');
    try {
      await apiService.updateFamilyMember(memberId, editId, {
        name:          editForm.name.trim(),
        relation:      editForm.relation,
        gender:        editForm.gender || null,
        date_of_birth: editForm.date_of_birth || null,
      });
      setEditId(null);
      onReload();
    } catch (e) { setError(e.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (fm) => {
    if (!window.confirm(`Remove ${fm.name} from this account?`)) return;
    try {
      await apiService.deleteFamilyMember(memberId, fm.id);
      onReload();
    } catch (e) { alert(e.message || 'Failed to remove.'); }
  };

  const inputCls = 'px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-full';

  if (family.length === 0) {
    return (
      <div className="text-center py-8">
        <Users className="w-8 h-8 text-slate-200 mx-auto mb-2" />
        <p className="text-slate-400 text-sm">No family members recorded</p>
        <button onClick={onAdd} className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium">+ Add family member</button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {family.map(fm => {
        const age = calcAge(fm.date_of_birth);
        const isEditing = editId === fm.id;
        return (
          <div key={fm.id} className={`rounded-xl border transition-colors ${isEditing ? 'border-blue-200 bg-blue-50' : 'border-slate-100 bg-slate-50 hover:bg-slate-100'}`}>
            {isEditing ? (
              <div className="p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Full name *" className={inputCls} />
                  <select value={editForm.relation} onChange={e => setEditForm(f => ({ ...f, relation: e.target.value }))} className={inputCls}>
                    <option value="">Relation *</option>
                    {RELATIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-slate-500 mb-0.5">Date of Birth</label>
                    <input type="date" value={editForm.date_of_birth}
                      onChange={e => setEditForm(f => ({ ...f, date_of_birth: e.target.value }))}
                      className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-0.5">Gender</label>
                    <select value={editForm.gender} onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))} className={inputCls}>
                      <option value="">—</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                {error && <p className="text-red-600 text-xs">{error}</p>}
                <div className="flex gap-2 pt-1">
                  <button onClick={cancelEdit} className="flex-1 px-3 py-1.5 border border-slate-200 text-xs rounded-lg hover:bg-white transition-colors">Cancel</button>
                  <button onClick={saveEdit} disabled={saving}
                    className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded-lg transition-colors">
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${fm.promoted_member_id ? 'bg-gradient-to-br from-teal-400 to-emerald-500' : 'bg-gradient-to-br from-blue-400 to-indigo-500'}`}>
                    {fm.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{fm.name}</p>
                    <p className="text-xs text-slate-400">
                      {RELATION_LABELS[fm.relation] || fm.relation}
                      {age !== null ? ` · Age: ${age}` : ''}
                    </p>
                    {fm.promoted_member_id && (
                      <p className="text-xs mt-0.5">
                        {fm.promoted_member_status === 'active' ? (
                          <span className="inline-flex items-center gap-1 text-teal-700 font-medium">
                            <UserPlus className="w-3 h-3" />
                            Account Holder · Ref: {fm.promoted_member_ref}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                            <UserPlus className="w-3 h-3" />
                            Pending Approval
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {fm.promoted_member_id ? (
                    <button onClick={() => navigate(`/members/${fm.promoted_member_id}`)} title="View account"
                      className="px-2 py-1 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-lg transition-colors text-xs font-medium">
                      View →
                    </button>
                  ) : (
                    <>
                      <button onClick={() => startEdit(fm)} title="Edit"
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(fm)} title="Remove"
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RecentPaymentsTab({ payments, summary, onReload }) {
  const { user } = useAuth();
  const isAdmin = canAdmin(user?.role);

  const [voidTarget, setVoidTarget] = useState(null); // payment object
  const [voidReason, setVoidReason]   = useState('');
  const [acting, setActing]           = useState(false);

  const sorted = [...payments].sort((a, b) => new Date(b.payment_month) - new Date(a.payment_month));

  if (sorted.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
        <CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">No payment records yet</p>
        <p className="text-slate-400 text-sm mt-1">Payment records will appear here once created</p>
      </div>
    );
  }

  const active = sorted.filter(p => p.approval_status !== 'voided');
  const totalPaid  = active.filter(p => p.status === 'paid').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const totalCount = active.filter(p => p.status === 'paid').length;
  const pendingCount = active.filter(p => p.approval_status === 'pending').length;

  const handleVoid = async () => {
    if (!voidReason.trim() || voidReason.trim().length < 5) return;
    setActing(true);
    try {
      await apiService.voidPayment(voidTarget.id, voidReason.trim());
      setVoidTarget(null);
      setVoidReason('');
      onReload();
    } catch (err) {
      alert(err.message || 'Failed to void payment');
    } finally {
      setActing(false);
    }
  };

  const handleConfirm = async (p) => {
    setActing(true);
    try {
      await apiService.confirmPayment(p.id);
      onReload();
    } catch (err) {
      alert(err.message || 'Failed to confirm payment');
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <p className="text-xs text-slate-500 mb-1">Total Records</p>
          <p className="text-slate-800 font-bold text-xl">{sorted.length}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="text-xs text-green-600 mb-1">Paid</p>
          <p className="text-green-700 font-bold text-xl">{totalCount}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
          <p className="text-xs text-blue-600 mb-1">Total Collected</p>
          <p className="text-blue-700 font-bold text-lg">{fmt(totalPaid)}</p>
        </div>
      </div>

      {pendingCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>{pendingCount} payment{pendingCount > 1 ? 's' : ''} awaiting approval</span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Month</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Method</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Paid Date</th>
              {isAdmin && <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map(p => {
              const d = new Date(p.payment_month);
              const isVoided  = p.approval_status === 'voided';
              const isPending = p.approval_status === 'pending';
              return (
                <tr key={p.id} className={`transition-colors ${isVoided ? 'opacity-50 bg-slate-50' : 'hover:bg-slate-50'}`}>
                  <td className="px-5 py-3 font-medium text-slate-800">
                    {MONTHS[d.getMonth()]} {d.getFullYear()}
                  </td>
                  <td className={`px-4 py-3 text-slate-700 ${isVoided ? 'line-through' : ''}`}>{fmt(p.amount)}</td>
                  <td className="px-4 py-3 text-slate-500 hidden sm:table-cell capitalize">
                    {p.payment_method ? p.payment_method.replace(/_/g, ' ') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {!isVoided && <StatusBadge status={p.status} />}
                      <ApprovalBadge approval_status={p.approval_status} void_reason={p.void_reason} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell">
                    {p.paid_date ? new Date(p.paid_date).toLocaleDateString('en-GB') : '—'}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      {!isVoided && (
                        <div className="flex items-center gap-1.5">
                          {isPending && (
                            <button
                              onClick={() => handleConfirm(p)}
                              disabled={acting}
                              className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg transition-colors disabled:opacity-50"
                            >
                              Approve
                            </button>
                          )}
                          <button
                            onClick={() => { setVoidTarget(p); setVoidReason(''); }}
                            disabled={acting}
                            className="px-2 py-1 text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                          >
                            Void
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Void confirmation modal */}
      {voidTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">Void Payment</h3>
            <p className="text-sm text-slate-600">
              Void <strong>{fmt(voidTarget.amount)}</strong> for{' '}
              <strong>{MONTHS[new Date(voidTarget.payment_month).getMonth()]} {new Date(voidTarget.payment_month).getFullYear()}</strong>?
              This cannot be undone.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Reason <span className="text-red-500">*</span>
                <span className="text-slate-400 font-normal ml-1">(min 5 chars)</span>
              </label>
              <textarea
                value={voidReason}
                onChange={e => setVoidReason(e.target.value)}
                rows={3}
                placeholder="e.g. Duplicate entry, incorrect amount…"
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setVoidTarget(null)}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleVoid}
                disabled={acting || voidReason.trim().length < 5}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-xl disabled:opacity-50 transition-colors"
              >
                {acting ? 'Voiding…' : 'Void Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MemberProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [family, setFamily] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('details');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddFamilyOpen, setIsAddFamilyOpen] = useState(false);
  const [isDeceasedOpen, setIsDeceasedOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [rejectActing, setRejectActing] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, fam, pay, sum, brk] = await Promise.all([
        apiService.getMemberById(id),
        apiService.getFamilyMembers(id),
        apiService.getSubscriptionPayments(id).catch(() => []),
        apiService.getMemberSummary(id).catch(() => null),
        apiService.getContributionBreakdown(id).catch(() => null),
      ]);
      setMember(m);
      setFamily(Array.isArray(fam) ? fam : []);
      setPayments(Array.isArray(pay) ? pay : []);
      setSummary(sum);
      setBreakdown(brk);
    } catch {
      navigate('/members');
    } finally {
      setLoading(false);
    }
  };

  // Called after any family member add/edit/delete — recalculates amount then reloads
  const loadAfterFamilyChange = async () => {
    try { await apiService.recalculateContribution(id); } catch {}
    await load();
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const result = await apiService.recalculateContribution(id);
      await load();
      if (result.changed) {
        alert(`Contribution updated: £${result.oldAmount} → £${result.newAmount}\n${result.breakdown}`);
      } else {
        alert(`Contribution is already correct: ${result.breakdown}`);
      }
    } catch (e) {
      alert(e.message || 'Failed to recalculate');
    } finally {
      setRecalculating(false);
    }
  };

  const handleUpdate = async (data) => {
    await apiService.updateMember(id, data);
    await load();
    setIsEditOpen(false);
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate this member?')) return;
    await apiService.deactivateMember(id);
    navigate('/members');
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve this member application and set status to Active?')) return;
    await apiService.updateMemberStatus(id, 'active');
    await load();
  };

  const handleReject = () => {
    setRejectReason('');
    setRejectError('');
    setIsRejectOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 5) {
      setRejectError('Please enter a reason (at least 5 characters)');
      return;
    }
    setRejectActing(true);
    try {
      await apiService.rejectPromotion(id, rejectReason.trim());
      setIsRejectOpen(false);
      setRejectReason('');
      await load();
    } catch (err) {
      setRejectError(err.message || 'Failed to reject application');
    } finally {
      setRejectActing(false);
    }
  };

  const handleRecordDeceased = async (deceasedData) => {
    try {
      await apiService.recordDeceasedWithTransfer(deceasedData);
      setIsDeceasedOpen(false);
      navigate('/deceased');
    } catch (err) {
      alert(err.message || 'Failed to record deceased.');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-64 p-8">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-slate-400">Loading member…</p>
      </div>
    </div>
  );

  if (!member) return null;

  const statusColor = {
    active: 'bg-green-500',
    pending: 'bg-amber-500',
    inactive: 'bg-slate-400',
    rejected: 'bg-red-500',
  }[member.status] || 'bg-slate-400';

  const statusLabel = {
    active: 'Active', pending: 'Pending', inactive: 'Inactive', rejected: 'Rejected',
  }[member.status] || member.status;

  const lifetimePaid = summary?.donations?.lifetime ?? payments.filter(p => p.status === 'paid').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const isCompliant = member.compliance?.isFullyCompliant;
  const currentPayments = payments.filter(p => new Date(p.payment_month).getFullYear() >= 2026);

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => navigate('/members')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm transition-colors font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to Members
        </button>
        <div className="flex items-center gap-2">
          <ReportsDropdown member={member} family={family} payments={payments} />
          {member.status === 'pending' && (
            <>
              <button onClick={handleApprove}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm">
                ✓ Approve
              </button>
              <button onClick={handleReject}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-red-300 hover:bg-red-50 text-red-600 rounded-lg text-sm font-medium transition-colors shadow-sm">
                ✕ Reject
              </button>
            </>
          )}
          <button onClick={() => setIsEditOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm">
            <Edit className="w-4 h-4" /> Edit Member
          </button>
          {member.status === 'active' && (
            <button onClick={() => setIsDeceasedOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:bg-red-50 hover:border-red-200 text-slate-600 hover:text-red-600 rounded-lg text-sm font-medium transition-colors shadow-sm">
              <Skull className="w-4 h-4" />
              <span className="hidden sm:inline">Record Deceased</span>
            </button>
          )}
          <button onClick={handleDeactivate}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-red-200 hover:bg-red-50 text-red-600 rounded-lg text-sm font-medium transition-colors shadow-sm">
            <UserX className="w-4 h-4" />
            <span className="hidden sm:inline">Deactivate</span>
          </button>
        </div>
      </div>

      {/* Pending application banner */}
      {member.status === 'pending' && (
        <div className="flex items-center justify-between gap-3 mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse inline-block"></span>
            {member.promotedFromFamilyId
              ? 'Auto-created age-21 account — approve to activate membership, or reject with reason.'
              : 'Application pending review — approve to activate membership and create subscription.'}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={handleApprove}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors">
              ✓ Approve
            </button>
            <button onClick={handleReject}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors">
              ✕ Reject
            </button>
          </div>
        </div>
      )}

      {/* Hero card */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl text-white px-5 sm:px-8 py-6 mb-5 shadow-lg">
        <div className="flex items-start gap-4">
          <div className="relative flex-shrink-0">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center text-white font-bold text-xl">
              {member.name?.split(' ').slice(0,2).map(n=>n[0]).join('')}
            </div>
            <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 ${statusColor} rounded-full border-2 border-white`}></span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold truncate">
              {member.name}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="text-blue-200 text-sm">Ref: {member.membershipRef || '—'}</span>
              <span className="text-blue-300">·</span>
              <span className="text-blue-100 text-sm">{statusLabel}</span>
            </div>
          </div>
          <div className="text-right hidden sm:block flex-shrink-0">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-blue-200 text-xs">Monthly</p>
                <p className="text-white font-bold text-lg">{fmt(summary?.paymentStatus?.amount ?? member.contribution)}</p>
              </div>
              <div>
                <p className="text-blue-200 text-xs">Lifetime</p>
                <p className="text-white font-bold text-lg">{fmt(lifetimePaid)}</p>
              </div>
              <div>
                <p className="text-blue-200 text-xs">Family</p>
                <p className="text-white font-bold text-lg">{family.length}</p>
              </div>
              <div>
                <p className="text-blue-200 text-xs">Compliance</p>
                <p className={`font-bold text-sm ${isCompliant ? 'text-green-300' : 'text-amber-300'}`}>
                  {isCompliant ? 'OK' : 'Action'}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile stats row */}
        <div className="flex gap-4 mt-4 sm:hidden pt-3 border-t border-white/20">
          <div className="text-center flex-1">
            <p className="text-blue-200 text-xs">Monthly</p>
            <p className="text-white font-bold">{fmt(summary?.paymentStatus?.amount ?? member.contribution)}</p>
          </div>
          <div className="text-center flex-1">
            <p className="text-blue-200 text-xs">Family</p>
            <p className="text-white font-bold">{family.length}</p>
          </div>
          <div className="text-center flex-1">
            <p className="text-blue-200 text-xs">Lifetime</p>
            <p className="text-white font-bold">{fmt(lifetimePaid)}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-t-xl border border-b-0 border-slate-200 flex overflow-x-auto shadow-sm">
        <TabButton active={tab === 'details'}  onClick={() => setTab('details')}  icon={Users}       label="Details & Family" />
        <TabButton active={tab === 'payments'} onClick={() => setTab('payments')} icon={CreditCard}  label="Recent Payments" />
        <TabButton active={tab === 'legacy'}   onClick={() => setTab('legacy')}   icon={Clock}       label="Legacy History" />
        <TabButton active={tab === 'current'}  onClick={() => setTab('current')}  icon={TrendingUp}  label="Current (2026+)" />
        <TabButton active={tab === 'notes'}    onClick={() => setTab('notes')}    icon={FileText}    label="Notes" />
      </div>

      {/* Tab content */}
      <div className="bg-slate-50 border border-slate-200 rounded-b-xl p-4 sm:p-6 min-h-64">

        {/* ── Details & Family ── */}
        {tab === 'details' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* Personal info */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-5 py-3.5 rounded-t-xl flex items-center justify-between">
                  <h2 className="font-semibold text-white text-sm">Account Holder Information</h2>
                  <button onClick={() => setIsEditOpen(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-medium transition-colors">
                    <Edit className="w-3 h-3" /> Edit
                  </button>
                </div>
                <dl className="p-5 space-y-3.5">
                  {member.email && (
                    <div className="flex items-start gap-3">
                      <Mail className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div><dt className="text-xs text-slate-400 mb-0.5">Email</dt><dd className="text-sm text-slate-800">{member.email}</dd></div>
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div><dt className="text-xs text-slate-400 mb-0.5">Phone</dt><dd className="text-sm text-slate-800">{member.phone}</dd></div>
                    </div>
                  )}
                  {member.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div><dt className="text-xs text-slate-400 mb-0.5">Address</dt>
                        <dd className="text-sm text-slate-800">{member.address}{member.postcode ? `, ${member.postcode}` : ''}</dd>
                      </div>
                    </div>
                  )}
                  {member.joinDate && (
                    <div className="flex items-start gap-3">
                      <Calendar className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div><dt className="text-xs text-slate-400 mb-0.5">Joined</dt>
                        <dd className="text-sm text-slate-800">{new Date(member.joinDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</dd>
                      </div>
                    </div>
                  )}
                  {member.date_of_birth && (
                    <div className="flex items-start gap-3">
                      <Calendar className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div><dt className="text-xs text-slate-400 mb-0.5">Date of Birth</dt>
                        <dd className="text-sm text-slate-800">{new Date(member.date_of_birth).toLocaleDateString('en-GB')}</dd>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <BadgeCheck className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <dt className="text-xs text-slate-400 mb-0.5">Monthly Contribution</dt>
                      <dd className="text-sm font-bold text-emerald-600">{fmt(summary?.paymentStatus?.amount ?? member.contribution)}</dd>
                      {breakdown && (
                        <p className="text-xs text-slate-400 mt-0.5">{breakdown.breakdown}
                          {breakdown.unknownAgeCount > 0 && (
                            <span className="text-amber-500 ml-1">· {breakdown.unknownAgeCount} no DOB</span>
                          )}
                        </p>
                      )}
                    </div>
                    {member.status === 'active' && (
                      <button onClick={handleRecalculate} disabled={recalculating}
                        className="text-xs text-blue-500 hover:text-blue-700 font-medium disabled:opacity-50 whitespace-nowrap mt-0.5">
                        {recalculating ? '…' : 'Recalculate'}
                      </button>
                    )}
                  </div>
                  {member.paymentMethod && (
                    <div className="flex items-start gap-3">
                      <CreditCard className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div><dt className="text-xs text-slate-400 mb-0.5">Payment Method</dt>
                        <dd className="text-sm text-slate-800 capitalize">{member.paymentMethod.replace(/_/g, ' ')}</dd>
                      </div>
                    </div>
                  )}
                  {member.marital_status && (
                    <div className="flex items-start gap-3">
                      <Users className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div><dt className="text-xs text-slate-400 mb-0.5">Marital Status</dt>
                        <dd className="text-sm text-slate-800 capitalize">{member.marital_status}</dd>
                      </div>
                    </div>
                  )}
                </dl>
              </div>

              {/* Family members */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-5 py-3.5 rounded-t-xl flex items-center justify-between">
                  <h2 className="font-semibold text-white text-sm">Family Members ({family.length})</h2>
                  <button onClick={() => setIsAddFamilyOpen(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-medium transition-colors">
                    <UserPlus className="w-3 h-3" /> Add
                  </button>
                </div>
                <div className="p-5">
                  <FamilyMemberCards
                    memberId={id}
                    family={family}
                    onReload={loadAfterFamilyChange}
                    onAdd={() => setIsAddFamilyOpen(true)}
                  />
                </div>
              </div>
            </div>

            {/* Compliance */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className={`px-5 py-3.5 flex items-center justify-between ${isCompliant ? 'bg-gradient-to-r from-green-600 to-emerald-700' : 'bg-gradient-to-r from-amber-500 to-orange-600'}`}>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-white" />
                  <h2 className="font-semibold text-white text-sm">Compliance & Declaration</h2>
                </div>
                <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${isCompliant ? 'bg-white/20 text-white' : 'bg-white/20 text-white'}`}>
                  {isCompliant ? '✓ COMPLIANT' : '⚠ ACTION REQUIRED'}
                </span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { key: 'agreedTerms',          label: 'Terms & Conditions' },
                    { key: 'declaredTruthful',      label: 'Truthful Declaration' },
                    { key: 'gdprConsent',           label: 'GDPR Consent' },
                    { key: 'contactAuthorisation',  label: 'Contact Authorisation' },
                  ].map(({ key, label }) => {
                    const ok = member.compliance?.[key];
                    return (
                      <div key={key} className={`flex items-center gap-2 p-2.5 rounded-lg text-sm ${ok ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                        {ok
                          ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          : <XCircle    className="w-4 h-4 text-red-400 flex-shrink-0" />
                        }
                        <span className={`text-xs font-medium ${ok ? 'text-green-700' : 'text-red-700'}`}>{label}</span>
                      </div>
                    );
                  })}
                </div>
                {member.compliance?.declarationDate && (
                  <p className="text-slate-400 text-xs mt-3">
                    Declaration signed: {new Date(member.compliance.declarationDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
                {!isCompliant && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <p className="text-amber-700 text-xs">
                      This member has not completed all compliance declarations. Explicit consent is required before processing personal data.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Recent Payments ── */}
        {tab === 'payments' && (
          <RecentPaymentsTab payments={payments} summary={summary} onReload={load} />
        )}

        {/* ── Legacy History ── */}
        {tab === 'legacy' && (
          <LegacyHistoryTab memberId={id} payments={payments} onReload={load} />
        )}

        {/* ── Current (2026+) ── */}
        {tab === 'current' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Current Payments (2026+)</h2>
              <div className="text-sm text-slate-500">
                {currentPayments.filter(p => p.status === 'paid').length} paid · {fmt(currentPayments.filter(p => p.status === 'paid').reduce((s, p) => s + parseFloat(p.amount || 0), 0))}
              </div>
            </div>
            <PaymentHistoryView
              memberId={id}
              subscription={member}
              onPaymentMarked={load}
            />
          </div>
        )}

        {/* ── Notes ── */}
        {tab === 'notes' && (
          <MemberNotes memberId={id} />
        )}
      </div>

      {/* Modals */}
      {isEditOpen && (
        <Modal onClose={() => setIsEditOpen(false)}>
          <MemberEditForm
            member={member}
            onUpdateMember={handleUpdate}
            onCancel={() => setIsEditOpen(false)}
          />
        </Modal>
      )}
      {isAddFamilyOpen && (
        <Modal onClose={() => setIsAddFamilyOpen(false)}>
          <AddFamilyMemberForm
            memberId={id}
            onSaved={() => { loadAfterFamilyChange(); setIsAddFamilyOpen(false); }}
            onCancel={() => setIsAddFamilyOpen(false)}
          />
        </Modal>
      )}
      {isDeceasedOpen && (
        <Modal onClose={() => setIsDeceasedOpen(false)}>
          <RecordDeceasedForm
            preselectedMember={{ id: member.id, name: member.name }}
            onRecordDeceased={handleRecordDeceased}
            onCancel={() => setIsDeceasedOpen(false)}
          />
        </Modal>
      )}

      {/* Reject promotion modal */}
      {isRejectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Reject Application</h3>
            <p className="text-slate-500 text-sm mb-4">
              This will set <strong>{member.name}</strong> to{' '}
              <span className="text-red-600 font-semibold">Rejected</span>.
              {member.promotedFromFamilyId && (
                <span className="block mt-1 text-amber-700">
                  A note will be added to the parent account and the 30-day alert will be cleared.
                </span>
              )}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={e => { setRejectReason(e.target.value); setRejectError(''); }}
                placeholder="Enter reason for rejection…"
                rows={3}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
              {rejectError && <p className="text-red-500 text-xs mt-1">{rejectError}</p>}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setIsRejectOpen(false); setRejectReason(''); setRejectError(''); }}
                disabled={rejectActing}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={rejectActing}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                {rejectActing ? 'Rejecting…' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
